import { PRODUCT_CATEGORY_CONFIG } from './category-config'
import { PRODUCT_CATALOG } from '@/lib/products/catalog/products'

export type CategoryFieldConfig = {
  field: string
  categories: ReadonlyArray<{ value: string; label: string }>
}

export function isCategoryFieldConfig(value: unknown): value is CategoryFieldConfig {
  if (typeof value !== 'object' || value === null) return false
  if (!('field' in value) || typeof value.field !== 'string') return false
  if (!('categories' in value) || !Array.isArray(value.categories)) return false
  return value.categories.every(
    (category: unknown) =>
      typeof category === 'object' &&
      category !== null &&
      'value' in category &&
      typeof category.value === 'string' &&
      'label' in category &&
      typeof category.label === 'string'
  )
}

export function categoryValueToSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+x\s+/g, 'x')
    .replace(/\s+/g, '-')
    .replace(/\//g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/--+/g, '-')
    .trim()
}

export function buildSubcategoryPath(productSlug: string, categoryValue: string): string {
  const slug = categoryValueToSlug(categoryValue)
  return `/listing/category/${productSlug}/${slug}`
}

export interface CategoryInfo {
  productType: string
  productSlug: string
  fieldName: string
  fieldValue: string
  fieldLabel: string
}

const PRODUCT_SLUG_TO_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(PRODUCT_CATALOG).map(([metadataKey, config]) => [
    config.urlSlug,
    metadataKey,
  ])
)

const PATH_BY_FACET = new Map<string, string>()

function facetKey(productSlug: string, fieldName: string, fieldValue: string): string {
  return `${productSlug}|${fieldName}|${fieldValue}`
}

function buildSlugIndex(): Map<string, CategoryInfo> {
  const index = new Map<string, CategoryInfo>()
  const entries: Array<{ productSlug: string; productType: string; fieldName: string; value: string; label: string; valueSlug: string }> = []

  for (const [productType, productConfig] of Object.entries(PRODUCT_CATEGORY_CONFIG)) {
    const productSlug = Object.keys(PRODUCT_SLUG_TO_TYPE).find(
      (key) => PRODUCT_SLUG_TO_TYPE[key] === productType
    )
    if (!productSlug) continue
    for (const [fieldName, fieldConfig] of Object.entries(productConfig)) {
      if (!isCategoryFieldConfig(fieldConfig)) continue
      for (const category of fieldConfig.categories) {
        entries.push({
          productSlug,
          productType,
          fieldName,
          value: category.value,
          label: category.label,
          valueSlug: categoryValueToSlug(category.value),
        })
      }
    }
  }

  const lastOwner = new Map<string, string>()
  for (const entry of entries) lastOwner.set(`${entry.productSlug}:${entry.valueSlug}`, entry.fieldName)

  for (const entry of entries) {
    const bareKey = `${entry.productSlug}:${entry.valueSlug}`
    const slug = lastOwner.get(bareKey) === entry.fieldName ? entry.valueSlug : `${entry.fieldName}-${entry.valueSlug}`
    PATH_BY_FACET.set(
      facetKey(entry.productSlug, entry.fieldName, entry.value),
      `/listing/category/${entry.productSlug}/${slug}`,
    )
    index.set(`${entry.productSlug}:${slug}`, {
      productType: entry.productType,
      productSlug: entry.productSlug,
      fieldName: entry.fieldName,
      fieldValue: entry.value,
      fieldLabel: entry.label,
    })
  }

  return index
}

let _slugIndexCache: Map<string, CategoryInfo> | null = null

function getSlugIndex(): Map<string, CategoryInfo> {
  if (!_slugIndexCache) {
    _slugIndexCache = buildSlugIndex()
  }
  return _slugIndexCache
}

export function subcategoryPathFor(
  productSlug: string,
  fieldName: string,
  fieldValue: string,
): string {
  getSlugIndex()
  return (
    PATH_BY_FACET.get(facetKey(productSlug, fieldName, fieldValue)) ??
    buildSubcategoryPath(productSlug, fieldValue)
  )
}

export function resolveSlugToCategory(
  subcategorySlug: string,
  productSlug: string
): CategoryInfo | null {
  const compositeKey = `${productSlug}:${subcategorySlug}`
  return getSlugIndex().get(compositeKey) || null
}

export function getAllSlugsForProduct(productSlug: string): string[] {
  const slugs: string[] = []

  for (const [compositeKey, info] of getSlugIndex().entries()) {
    if (info.productSlug === productSlug) {
      const subcategorySlug = compositeKey.split(':')[1]
      slugs.push(subcategorySlug)
    }
  }

  return slugs
}

export function getAllSubcategoryParams(): Array<{ productSlug: string; subcategorySlug: string }> {
  const params: Array<{ productSlug: string; subcategorySlug: string }> = []

  for (const [compositeKey, info] of getSlugIndex().entries()) {
    const subcategorySlug = compositeKey.split(':')[1]
    params.push({
      productSlug: info.productSlug,
      subcategorySlug: subcategorySlug,
    })
  }

  return params
}
